/* =========================================================================
   Temple Town — hand-authored scripts (loaded on every page, deferred)
   ========================================================================= */

/* -------------------------------------------------------------------------
   Hero slideshow current-slide counter fix
   -------------------------------------------------------------------------
   The theme's section-slideshow build JS only wires up the "01" current-slide
   label (.js-progress-first-label) when an autoplay progress bar
   (.js-progressbar) is rendered. With arrows-only navigation (no progress bar)
   that condition is false, so the listener is never attached and the counter
   stays frozen at "01" while the slides change. Build files must not be edited,
   so keep the label in sync here by watching which slide carries Swiper's
   active class. Safe to run alongside the theme handler — it writes the same
   value.
--------------------------------------------------------------------------- */
(function () {
	function pad(n) {
		return n > 9 ? String(n) : "0" + n;
	}

	function initSlideCounter(section) {
		var label = section.querySelector(".js-progress-first-label");
		var slider = section.querySelector(".js-slider") || section;

		if (!label || !slider) {
			return;
		}

		function currentNumber() {
			var active = slider.querySelector(".swiper-slide-active");

			if (!active) {
				return null;
			}

			// Loop mode: Swiper marks every slide with its real index.
			var dataIndex = active.getAttribute("data-swiper-slide-index");

			if (dataIndex !== null) {
				return parseInt(dataIndex, 10) + 1;
			}

			// Non-loop: fall back to DOM position among the real slides.
			var slides = Array.prototype.slice.call(
				slider.querySelectorAll(".swiper-slide")
			);

			return slides.indexOf(active) + 1;
		}

		function update() {
			var n = currentNumber();

			if (n) {
				label.innerText = pad(n);
			}
		}

		var target = slider.querySelector(".swiper-wrapper") || slider;

		new MutationObserver(update).observe(target, {
			attributes: true,
			attributeFilter: ["class"],
			subtree: true
		});

		// Sync once for the initial active slide (in case Swiper is already up).
		update();
	}

	function run() {
		Array.prototype.forEach.call(
			document.querySelectorAll(".js-slideshow"),
			initSlideCounter
		);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", run);
	} else {
		run();
	}
})();

/* -------------------------------------------------------------------------
   Lenis smooth scroll — desktop only
   -------------------------------------------------------------------------
   Adds refined smooth scrolling on desktop pointers only. The library is
   loaded from a pinned CDN URL with a Subresource Integrity hash, and only
   when the guards pass — so phones, tablets, touch devices and visitors who
   asked for reduced motion never download or run it.

   It also yields to overlays in two ways:

   1. Every overlay that owns its own scrollbar (drawers, modals, quick view,
      the newsletter / age-check / notify-me / ask-a-question popups, the
      cookie bar, mega menu…) is tagged with `data-lenis-prevent`. Lenis
      listens for the wheel on `window` and calls preventDefault, so without
      that tag the wheel scrolls the PAGE BEHIND the popup instead of the
      popup's content — the popup looks frozen. The tag tells Lenis to leave
      the event alone and let the browser scroll the popup natively.
   2. When the theme hard-locks the page (`body.scroll-padding-0`, or an
      inline `body { overflow: hidden }` from its scroll-lock) Lenis is paused
      entirely and resumed on close.

   Popups injected later (section re-render, quick view, cart drawer, apps)
   are picked up by a DOM observer, plus a capture-phase wheel listener that
   tags any scrollable element sitting in an overlay context the moment it is
   first scrolled — capture runs before Lenis' own listener, so the tag is
   honoured on that very event.

   Lenis v1 drives the real document scroll position, so IntersectionObserver,
   the sticky header and scroll animations keep working normally.
--------------------------------------------------------------------------- */
(function () {
	var LENIS_SRC = "https://cdn.jsdelivr.net/npm/lenis@1.1.20/dist/lenis.min.js";
	var LENIS_SRI =
		"sha384-eD5ubmuCcvTdCADWuSchJYE7wcj2nzNr0itbrh6w/KJyBiltSZ8w3VFRBSWu5YQ8";
	var LOCK_CLASS = "scroll-padding-0";
	var PREVENT_ATTR = "data-lenis-prevent";

	// Known overlay containers that scroll on their own.
	var OVERLAY_SCROLLERS = [
		".modal",
		".modal__card",
		".modal__body",
		".drawer",
		".drawer__body",
		".newsletter-popup",
		".newsletter-popup-new",
		".age-check-popup",
		".age-check-popup-new",
		".ask-question-popup",
		".ask-question-popup-new",
		".product-notify-me-popup",
		".product-notify-me-popup-new",
		".cookie-bar",
		".cookie-bar-new",
		".cart-notification__body",
		".compare-products__body",
		".filter-list-new",
		".header-mobile__menu-list-wrapper",
		".header-mega-menu__content",
		".disclosure__list"
	].join(",");

	// "This subtree is an overlay" markers — used by the runtime fallback so
	// popups we don't know about (apps, future sections) are covered too.
	var OVERLAY_ROOTS = [
		".modal",
		".drawer",
		".popup",
		'[class*="popup"]',
		'[role="dialog"]',
		'[aria-modal="true"]',
		"dialog"
	].join(",");

	var mqDesktop = window.matchMedia("(min-width: 1024px)");
	var mqFinePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
	var mqReduced = window.matchMedia("(prefers-reduced-motion: reduce)");

	var lenis = null;
	var rafId = null;
	var loading = false;
	var lockObserver = null;
	var domObserver = null;
	var markScheduled = false;
	var lastProbed = null;

	function shouldEnable() {
		return mqDesktop.matches && mqFinePointer.matches && !mqReduced.matches;
	}

	function mark(element) {
		if (element && element.nodeType === 1 && !element.hasAttribute(PREVENT_ATTR)) {
			element.setAttribute(PREVENT_ATTR, "");
		}
	}

	function markKnownOverlays() {
		Array.prototype.forEach.call(
			document.querySelectorAll(OVERLAY_SCROLLERS),
			mark
		);
	}

	function hasAddedElements(mutations) {
		for (var i = 0; i < mutations.length; i++) {
			var added = mutations[i].addedNodes;

			for (var j = 0; j < added.length; j++) {
				if (added[j].nodeType === 1) {
					return true;
				}
			}
		}

		return false;
	}

	function scheduleMark(mutations) {
		if (markScheduled || (mutations && !hasAddedElements(mutations))) {
			return;
		}
		markScheduled = true;
		window.requestAnimationFrame(function () {
			markScheduled = false;
			lastProbed = null;
			markKnownOverlays();
		});
	}

	function scrollsVertically(element) {
		if (element.scrollHeight - element.clientHeight <= 1) {
			return false;
		}
		var overflowY = window.getComputedStyle(element).overflowY;

		return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
	}

	function isInOverlay(element) {
		for (var node = element; node && node !== document.body; node = node.parentElement) {
			if (node.matches && node.matches(OVERLAY_ROOTS)) {
				return true;
			}
			if (window.getComputedStyle(node).position === "fixed") {
				return true;
			}
		}

		return false;
	}

	// Capture phase on document runs before Lenis' bubbling window listener,
	// so anything tagged here takes effect on this same wheel event. Probing
	// is skipped while the pointer stays on the element already checked.
	function tagScrollerUnderPointer(event) {
		var node = event.target;
		var depth = 0;

		if (node === lastProbed) {
			return;
		}
		lastProbed = node;

		while (node && node.nodeType === 1 && node !== document.body && depth < 15) {
			if (node.hasAttribute(PREVENT_ATTR)) {
				return;
			}
			if (scrollsVertically(node) && isInOverlay(node)) {
				mark(node);
				return;
			}
			node = node.parentElement;
			depth += 1;
		}
	}

	// The theme locks the page either with the class or, via its scroll-lock,
	// with an inline overflow on <body>. Treat both as "an overlay owns the
	// scroll now".
	function isPageLocked() {
		return (
			document.body.classList.contains(LOCK_CLASS) ||
			document.body.style.overflow === "hidden" ||
			document.documentElement.style.overflow === "hidden"
		);
	}

	function loadLibrary(done) {
		if (window.Lenis) {
			done();
			return;
		}
		if (loading) {
			return;
		}
		loading = true;

		var script = document.createElement("script");
		script.src = LENIS_SRC;
		script.integrity = LENIS_SRI;
		script.crossOrigin = "anonymous";
		script.defer = true;
		script.onload = function () {
			loading = false;
			done();
		};
		script.onerror = function () {
			loading = false;
		};
		document.head.appendChild(script);
	}

	function syncWithScrollLock() {
		if (!lenis) {
			return;
		}
		if (isPageLocked()) {
			lenis.stop();
		} else {
			lenis.start();
		}
	}

	function start() {
		if (lenis || !window.Lenis || !shouldEnable()) {
			return;
		}

		markKnownOverlays();

		lenis = new window.Lenis({
			lerp: 0.1,
			smoothWheel: true,
			wheelMultiplier: 1
		});

		function raf(time) {
			lenis.raf(time);
			rafId = window.requestAnimationFrame(raf);
		}
		rafId = window.requestAnimationFrame(raf);

		// Tag overlays rendered after load (quick view, cart drawer, section
		// re-render, app popups) and anything scrolled for the first time.
		document.addEventListener("wheel", tagScrollerUnderPointer, true);
		domObserver = new MutationObserver(scheduleMark);
		domObserver.observe(document.body, { childList: true, subtree: true });

		// Pause while a drawer/menu/cart holds the page locked, resume on close.
		syncWithScrollLock();
		lockObserver = new MutationObserver(syncWithScrollLock);
		lockObserver.observe(document.body, {
			attributes: true,
			attributeFilter: ["class", "style"]
		});
		lockObserver.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["style"]
		});
	}

	function stop() {
		document.removeEventListener("wheel", tagScrollerUnderPointer, true);
		lastProbed = null;

		if (domObserver) {
			domObserver.disconnect();
			domObserver = null;
		}
		if (lockObserver) {
			lockObserver.disconnect();
			lockObserver = null;
		}
		if (rafId) {
			window.cancelAnimationFrame(rafId);
			rafId = null;
		}
		if (lenis) {
			lenis.destroy();
			lenis = null;
		}
	}

	function update() {
		if (shouldEnable()) {
			loadLibrary(start);
		} else {
			stop();
		}
	}

	function watch(mq) {
		if (mq.addEventListener) {
			mq.addEventListener("change", update);
		} else if (mq.addListener) {
			mq.addListener(update);
		}
	}
	watch(mqDesktop);
	watch(mqFinePointer);
	watch(mqReduced);

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", update);
	} else {
		update();
	}
})();

/* -------------------------------------------------------------------------
   Category thumbnails — native horizontal scroll on mobile
   -------------------------------------------------------------------------
   The theme renders the .wac_categories_thumbs collection-list as a
   1-per-view Swiper on phones. We want a simple horizontal scroll row of
   small circular thumbnails instead, so destroy that Swiper on mobile and let
   CSS (wac-custom.css) drive a native overflow-x scroller. The Swiper is
   re-created by the theme when crossing the 768px breakpoint, so re-run after
   a short delay (its re-init is synchronous, ours is deferred) to catch it.
--------------------------------------------------------------------------- */
(function () {
	var mq = window.matchMedia("(max-width: 767px)");

	function unswipeCategories() {
		if (!mq.matches) {
			return;
		}
		Array.prototype.forEach.call(
			document.querySelectorAll(".wac_categories_thumbs .collection-list__slider"),
			function (el) {
				if (el.swiper && !el.swiper.destroyed) {
					el.swiper.destroy(true, true);
				}
			}
		);
	}

	function schedule() {
		window.setTimeout(unswipeCategories, 80);
	}

	if (window.themeCore && window.themeCore.loaded) {
		schedule();
	} else {
		document.addEventListener("theme:all:loaded", schedule, { once: true });
	}

	if (mq.addEventListener) {
		mq.addEventListener("change", schedule);
	} else if (mq.addListener) {
		mq.addListener(schedule);
	}
})();

/* -------------------------------------------------------------------------
   Hero pagination — move it below the hero on mobile
   -------------------------------------------------------------------------
   The theme renders the slideshow pagination inside the slider. On mobile we
   want the dots to sit BELOW the hero section instead. Swiper keeps updating
   the element by reference, so we can relocate it in the DOM after init and it
   still tracks the active slide. On desktop it stays inside (and CSS hides it,
   since desktop uses the 01 / 03 counter).
--------------------------------------------------------------------------- */
(function () {
	var mq = window.matchMedia("(max-width: 767px)");

	function moveHeroPagination() {
		var sec = document.querySelector(".wac_home_slider");
		if (!sec) {
			return;
		}
		// .wac_home_slider is on .slideshow-new__slider-wrapper; the pagination is
		// its SIBLING inside the parent .slideshow-new__slider — not a descendant.
		var slider = sec.closest(".slideshow-new__slider");
		var wrap = sec.closest(".shopify-section") || slider || sec;
		var pag = (slider && slider.querySelector(".slideshow-new__pagination"))
			|| document.querySelector(".wac-hero-pagination-moved");
		if (!pag) {
			return;
		}

		if (mq.matches) {
			if (pag.previousElementSibling !== wrap) {
				wrap.insertAdjacentElement("afterend", pag);
			}
			pag.classList.add("wac-hero-pagination-moved");
		} else {
			if (slider && pag.parentElement !== slider) {
				slider.appendChild(pag);
			}
			pag.classList.remove("wac-hero-pagination-moved");
		}
	}

	function schedule() {
		window.setTimeout(moveHeroPagination, 120);
	}

	if (window.themeCore && window.themeCore.loaded) {
		schedule();
	} else {
		document.addEventListener("theme:all:loaded", schedule, { once: true });
	}

	if (mq.addEventListener) {
		mq.addEventListener("change", schedule);
	}
})();

/* -------------------------------------------------------------------------
   Header "stuck" state flag
   -------------------------------------------------------------------------
   We want the mobile header's shadow + bottom border only once the page is
   scrolled, not while it rests at the top. The theme's .header-section--top-position
   class can't drive this: its remover only runs on scroll for TRANSPARENT
   headers, and this header is solid — so the class sticks and never clears.
   We toggle our own .wac-header-stuck on <html> from scrollY; the CSS
   (wac-custom.css, mobile only) hides the shadow/border at rest and restores
   them while stuck.
--------------------------------------------------------------------------- */
(function () {
	var root = document.documentElement;
	var ticking = false;

	function update() {
		ticking = false;
		var scrolled = (window.scrollY || window.pageYOffset || 0) > 0;
		root.classList.toggle("wac-header-stuck", scrolled);
	}

	function onScroll() {
		if (!ticking) {
			ticking = true;
			window.requestAnimationFrame(update);
		}
	}

	update();
	window.addEventListener("scroll", onScroll, { passive: true });
	window.addEventListener("resize", onScroll, { passive: true });
})();

/* ------------------------------------------------------------------
   Search results — "3 / 4 per row" grid-view toggle.
   Mirrors the collection page's data-grid-col mechanism, scoped to the
   search section so it never double-binds on collection pages (which
   have their own handler). Persists the choice in localStorage. */
(function () {
	"use strict";

	function initSearchGridView() {
		var scope = document.querySelector(".js-search");
		if (!scope) {
			return;
		}

		var buttons = scope.querySelectorAll(".js-search-grid-btn");
		var grid = scope.querySelector(".js-grid-wrapper");

		if (!buttons.length || !grid) {
			return;
		}

		var STORAGE_KEY = "search-grid-cols";

		function apply(cols) {
			grid.setAttribute("data-grid-col", cols);

			buttons.forEach(function (button) {
				button.classList.toggle(
					"is-active",
					button.getAttribute("data-grid-cols") === cols
				);
			});
		}

		var saved = localStorage.getItem(STORAGE_KEY);
		if (saved) {
			apply(saved);
		}

		buttons.forEach(function (button) {
			button.addEventListener("click", function () {
				if (button.classList.contains("is-active")) {
					return;
				}

				var cols = button.getAttribute("data-grid-cols");
				apply(cols);
				localStorage.setItem(STORAGE_KEY, cols);
			});
		});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initSearchGridView);
	} else {
		initSearchGridView();
	}
})();
